import {
  AudioQuality,
  AudioSettings,
  VoiceConnection,
  ConnectionState,
  AudioDevice,
  RTCSignalData,
} from "../types/audio";

const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  inputDevice: "default",
  outputDevice: "default",
  quality: AudioQuality.MEDIUM,
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  pushToTalk: false,
  pushToTalkKey: "Space",
};

export class VoiceManager {
  private connections: Map<string, VoiceConnection> = new Map();
  private localStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private settings: AudioSettings = DEFAULT_AUDIO_SETTINGS;
  private sessionId: string = "";
  private userId: string = "";
  private signalCallback: ((_data: RTCSignalData) => void) | null = null;
  private onConnectionStateChange: ((_userId: string, _state: ConnectionState) => void) | null =
    null;
  private onRemoteStream: ((_userId: string, _stream: MediaStream) => void) | null = null;
  private isMuted: boolean = false;
  private pushToTalkPressed: boolean = false;

  // ICE servers configuration (STUN server)
  private iceServers: RTCIceServer[] = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ];

  constructor() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
  }

  /**
   * Initialize voice chat for a session
   */
  async initializeVoiceChat(sessionId: string, userId: string): Promise<void> {
    this.sessionId = sessionId;
    this.userId = userId;

    // Initialize audio context
    this.audioContext = new AudioContext();

    // Get user media with current settings
    await this.requestMediaPermissions();

    // Set up push-to-talk if enabled
    if (this.settings.pushToTalk) {
      this.setupPushToTalk();
    }
  }

  /**
   * Request microphone permissions and get media stream
   */
  private async requestMediaPermissions(): Promise<void> {
    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: this.settings.echoCancellation,
          noiseSuppression: this.settings.noiseSuppression,
          autoGainControl: this.settings.autoGainControl,
          deviceId:
            this.settings.inputDevice !== "default"
              ? { exact: this.settings.inputDevice }
              : undefined,
        },
        video: false,
      };

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Mute by default if push-to-talk is enabled
      if (this.settings.pushToTalk) {
        this.setMuted(true);
      }
    } catch (error) {
      console.error("Failed to get user media:", error);
      throw new Error("Microphone access denied");
    }
  }

  /**
   * Connect to a participant
   */
  async connectToParticipant(userId: string, isInitiator: boolean = false): Promise<void> {
    if (!this.localStream) {
      throw new Error("Local stream not initialized");
    }

    // Create peer connection
    const peerConnection = new RTCPeerConnection({ iceServers: this.iceServers });

    const connection: VoiceConnection = {
      connectionId: `${this.sessionId}-${userId}`,
      userId,
      sessionId: this.sessionId,
      peerConnection,
      localStream: this.localStream,
      remoteStream: null,
      connectionState: ConnectionState.CONNECTING,
      isMuted: this.isMuted,
      volume: 1.0,
    };

    this.connections.set(userId, connection);

    // Add local stream tracks to peer connection
    this.localStream.getTracks().forEach((track) => {
      peerConnection.addTrack(track, this.localStream!);
    });

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.signalCallback) {
        this.signalCallback({
          type: "ice-candidate",
          targetUserId: userId,
          sourceUserId: this.userId,
          sessionId: this.sessionId,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    // Handle remote stream
    peerConnection.ontrack = (event) => {
      const [remoteStream] = event.streams;
      connection.remoteStream = remoteStream;
      this.onRemoteStream?.(userId, remoteStream);
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      const state = this.mapConnectionState(peerConnection.connectionState);
      connection.connectionState = state;
      this.onConnectionStateChange?.(userId, state);

      if (state === ConnectionState.FAILED) {
        this.handleConnectionFailure(userId);
      }
    };

    // Create offer if initiator
    if (isInitiator) {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      if (this.signalCallback) {
        this.signalCallback({
          type: "offer",
          targetUserId: userId,
          sourceUserId: this.userId,
          sessionId: this.sessionId,
          sdp: offer,
        });
      }
    }
  }

  /**
   * Handle incoming WebRTC signal
   */
  async handleSignal(signal: RTCSignalData): Promise<void> {
    const connection = this.connections.get(signal.sourceUserId);

    if (!connection) {
      // If we don't have a connection yet and received an offer, create one
      if (signal.type === "offer") {
        await this.connectToParticipant(signal.sourceUserId, false);
        const newConnection = this.connections.get(signal.sourceUserId);
        if (newConnection) {
          await this.processSignal(newConnection, signal);
        }
      }
      return;
    }

    await this.processSignal(connection, signal);
  }

  private async processSignal(connection: VoiceConnection, signal: RTCSignalData): Promise<void> {
    const { peerConnection } = connection;

    switch (signal.type) {
      case "offer":
        if (signal.sdp) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(signal.sdp));
          const answer = await peerConnection.createAnswer();
          await peerConnection.setLocalDescription(answer);

          if (this.signalCallback) {
            this.signalCallback({
              type: "answer",
              targetUserId: signal.sourceUserId,
              sourceUserId: this.userId,
              sessionId: this.sessionId,
              sdp: answer,
            });
          }
        }
        break;

      case "answer":
        if (signal.sdp) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(signal.sdp));
        }
        break;

      case "ice-candidate":
        if (signal.candidate) {
          await peerConnection.addIceCandidate(new RTCIceCandidate(signal.candidate));
        }
        break;
    }
  }

  /**
   * Disconnect from a participant
   */
  disconnectFromParticipant(userId: string): void {
    const connection = this.connections.get(userId);
    if (connection) {
      connection.peerConnection.close();
      this.connections.delete(userId);
    }
  }

  /**
   * Disconnect all participants and cleanup
   */
  async cleanup(): Promise<void> {
    // Close all peer connections
    this.connections.forEach((connection) => {
      connection.peerConnection.close();
    });
    this.connections.clear();

    // Stop local stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    // Close audio context
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    // Remove event listeners
    if (this.settings.pushToTalk) {
      this.removePushToTalk();
    }
  }

  /**
   * Mute/unmute microphone
   */
  setMuted(muted: boolean): void {
    this.isMuted = muted;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = !muted;
      });
    }
  }

  /**
   * Set volume for a specific participant
   */
  setParticipantVolume(userId: string, volume: number): void {
    const connection = this.connections.get(userId);
    if (connection) {
      connection.volume = Math.max(0, Math.min(1, volume));
      // Volume control would be applied to audio element playing the remote stream
    }
  }

  /**
   * Get available audio devices
   */
  async getAudioDevices(): Promise<{ inputs: AudioDevice[]; outputs: AudioDevice[] }> {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const inputs: AudioDevice[] = devices
      .filter((device) => device.kind === "audioinput")
      .map((device) => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
        kind: device.kind,
      }));

    const outputs: AudioDevice[] = devices
      .filter((device) => device.kind === "audiooutput")
      .map((device) => ({
        deviceId: device.deviceId,
        label: device.label || `Speaker ${device.deviceId.slice(0, 5)}`,
        kind: device.kind,
      }));

    return { inputs, outputs };
  }

  /**
   * Update audio settings
   */
  async updateSettings(settings: Partial<AudioSettings>): Promise<void> {
    const oldSettings = { ...this.settings };
    this.settings = { ...this.settings, ...settings };

    // If input device changed, restart media stream
    if (settings.inputDevice !== undefined && settings.inputDevice !== oldSettings.inputDevice) {
      await this.restartMediaStream();
    }

    // Update push-to-talk
    if (settings.pushToTalk !== undefined && settings.pushToTalk !== oldSettings.pushToTalk) {
      if (settings.pushToTalk) {
        this.setupPushToTalk();
        this.setMuted(true);
      } else {
        this.removePushToTalk();
        this.setMuted(false);
      }
    }
  }

  /**
   * Restart media stream with new settings
   */
  private async restartMediaStream(): Promise<void> {
    // Stop current stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
    }

    // Get new stream
    await this.requestMediaPermissions();

    // Update all peer connections with new stream
    this.connections.forEach((connection) => {
      const senders = connection.peerConnection.getSenders();
      const audioTrack = this.localStream?.getAudioTracks()[0];

      if (audioTrack) {
        const audioSender = senders.find((sender) => sender.track?.kind === "audio");
        if (audioSender) {
          audioSender.replaceTrack(audioTrack);
        }
      }
    });
  }

  /**
   * Setup push-to-talk controls
   */
  private setupPushToTalk(): void {
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
  }

  private removePushToTalk(): void {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.code === this.settings.pushToTalkKey && !this.pushToTalkPressed) {
      this.pushToTalkPressed = true;
      this.setMuted(false);
    }
  }

  private handleKeyUp(event: KeyboardEvent): void {
    if (event.code === this.settings.pushToTalkKey && this.pushToTalkPressed) {
      this.pushToTalkPressed = false;
      this.setMuted(true);
    }
  }

  /**
   * Handle connection failure
   */
  private handleConnectionFailure(userId: string): void {
    console.error(`Connection to ${userId} failed`);
    // Could implement reconnection logic here
  }

  /**
   * Map RTCPeerConnectionState to ConnectionState
   */
  private mapConnectionState(state: RTCPeerConnectionState): ConnectionState {
    switch (state) {
      case "connecting":
        return ConnectionState.CONNECTING;
      case "connected":
        return ConnectionState.CONNECTED;
      case "disconnected":
        return ConnectionState.DISCONNECTED;
      case "failed":
        return ConnectionState.FAILED;
      case "closed":
        return ConnectionState.DISCONNECTED;
      default:
        return ConnectionState.DISCONNECTED;
    }
  }

  // Getters
  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  isMicMuted(): boolean {
    return this.isMuted;
  }

  getConnections(): Map<string, VoiceConnection> {
    return this.connections;
  }

  // Setters for callbacks
  setSignalCallback(callback: (_data: RTCSignalData) => void): void {
    this.signalCallback = callback;
  }

  setConnectionStateChangeCallback(
    callback: (_userId: string, _state: ConnectionState) => void
  ): void {
    this.onConnectionStateChange = callback;
  }

  setRemoteStreamCallback(callback: (_userId: string, _stream: MediaStream) => void): void {
    this.onRemoteStream = callback;
  }
}

export default VoiceManager;
